import dedent from 'dedent';
import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'write-remote-files' as const;

const argumentZod = z.object({
  files: z
    .array(
      z.object({
        path: z
          .string()
          .describe(
            'The path of the file to write, relative to the project root (e.g. "types.ts", "utils/format.ts", "component-id-1.webflow.tsx")'
          ),
        content: z.string().describe('The content of the file to write'),
      })
    )
    .min(1)
    .describe('All files to write in this single call.'),
});

export const writeRemoteFilesTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Write components',
  prettyStatus: {
    executing: 'Creating components',
    executed: 'Created components',
    failed: 'Failed to create components',
    partial: 'Created components with errors',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Writes MULTIPLE files to the code component sandbox environment in a single call. Each file's path is relative to the project root.

      Use this tool — not repeated single-file writes — whenever you are creating more than one source file (e.g. a component plus shared \`types.ts\` / \`utils/format.ts\` / sub-components). Writing files one at a time is significantly slower and more token-expensive: each individual write pays its own type-check round trip, whereas this tool writes every file and type-checks the result ONCE.

      Prefer splitting conservatively: extract a file only when it is genuinely reusable or substantial. A handful of focused files (shared types, a couple of real sub-components, shared helpers) is almost always better than one file per tiny function.

      Only files containing text can be written by this tool.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  featureGate: FEATURE_FLAGS.VCC_MULTIFILE,
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  // Unlike write-remote-file (15s for one write + one type-check), this tool
  // does N sequential writes plus one install + type-check, and N isn't known
  // when the timeout is set -- there's no per-call hook to scale it with the
  // batch size. Matches DEFAULT_EXTERNAL_TOOL_TIMEOUT (convertToolsForAgent.ts),
  // the fallback already granted to any tool with no override, so a large
  // batch doesn't get less headroom than an unconfigured tool would.
  timeout: 60000,
});
